import React, { useEffect, useMemo, useState } from 'react';
// import ConfirmationPopup from '../../Modals/ConfirmationPopup';
import { Box, Grid2, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from '../../StockTransfer.module.css';
import { useReceivedList} from '../../../Queries/UseStockTransferQueries';
import { useStockTransferStore } from '../../../Store/useStockTransferstore';
import { Column, IndentDetail } from '../../Issue/Issue.helper';
import { ReceivedListResponse } from '../Receive.helper';
import { convertDateToDDMMYYYY, convertUnderscoreToTitle } from '../../../../../utils/commonUtils';
import ReceiveListTable from '../ReceiveListTable';
import useCurrency from '../../../../../components/CurrencyProvider';
import { useClinic } from '../../../../../components/ClinicProvider';

type ReceivedRow = {
  id: string | number;
  patient: string;
  indentNoDate: string;
  issueNoDate: string | Date;
  receivedNoDate: string | Date;
  fromClinicReceivedStore: string;
  toClinicIssuedStore: string;
  totalAmount?: string;
  receivingAgainst: React.ReactNode;
  indent_details?: IndentDetail[];
  receiveType?: React.ReactNode;
};

const ReceiveListComponent = () => {
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  const { currencySymbol } = useCurrency();
  
  const BaseProspectiveHeaders: Column<ReceivedRow>[] = [
    { label: tStockTransfer('Received No | Date'), field: 'receivedNoDate' },
    { label: tStockTransfer('Patient'), field: 'patient' },
    { label: tStockTransfer('Clinic | Received Store'), field: 'fromClinicReceivedStore' },
    { label: tStockTransfer('Clinic | Issued Store'), field: 'toClinicIssuedStore' },
    { label: `${tStockTransfer('Total Amount')} (${currencySymbol})`, field: 'totalAmount' },
    { label: tStockTransfer('Receiving Against'), field: 'receivingAgainst' },
    { label: tStockTransfer('Receive Type'), field: 'receiveType' },
  ];
  
  const stockTransferStore = useStockTransferStore();
  const { setProspectiveTotalCount } = useStockTransferStore();
  const [selectedTab, setSelectedTab] = useState('ALL');
  const [debouncedSearchText, setDebouncedSearchText] = useState(stockTransferStore.searchText);
  const [receivedTableData, setReceivedTableData] = useState<ReceivedRow[]>([]);
  const { ClinicId } = useClinic();
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(stockTransferStore.searchText);
    }, 600);
    return () => {
      clearTimeout(handler);
    };
  }, [stockTransferStore.searchText]);
  
  const getStatusChip = (rawStatus?: string) => {
    const key = (rawStatus ?? '').toUpperCase();
    const convertedStatus = convertUnderscoreToTitle(rawStatus ?? '');
    const label = convertedStatus ? tStockTransfer(convertedStatus) : '-';

    let color = '#757575';
    let bg = '#F5F5F5';

    switch (key) {
    case 'DIRECT':
      color = '#3A80E2';
      bg = '#E3F2FD';
      break;
    case 'INDENT':
      color = '#9045DB';
      bg = '#F3E5F5';
      break;
    case 'PR':
      color = '#E1CF32';
      bg = '#FFF3E0';
      break;
    }
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          color,
          backgroundColor: bg,
          fontWeight: 600,
          borderRadius: '18px',
          px: 1.5,
        }}
      />
    );
  };

  const getIssueAgainstChip = (isPatient: boolean) => {
    const label = isPatient ? tStockTransfer('Patient') : tStockTransfer('Store');
    const color = isPatient ? '#FB8C00' : '#2E7D32';
    const bg = isPatient ? '#FFF3E0' : '#E8F5E9';
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          color,
          backgroundColor: bg,
          fontWeight: 600,
          borderRadius: '18px',
          // border: `1px solid ${color}`,
          px: 1.5,
        }}
      />
    );
  };
     
  const ReceiveListParams = useMemo(() => ({
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    status: selectedTab !== 'ALL' ? selectedTab : undefined,
    search: debouncedSearchText ? debouncedSearchText : undefined,
    // Only apply filter values when Apply is clicked (trigger changes)
    created_on_after: stockTransferStore.fromDate ? stockTransferStore.fromDate : undefined,
    created_on_before: stockTransferStore.toDate ? stockTransferStore.toDate : undefined,
    from_store : stockTransferStore.fromStoreId ? stockTransferStore.fromStoreId : undefined,
    to_store: stockTransferStore.toStoreId ? stockTransferStore.toStoreId : undefined,
    clinic: ClinicId ? ClinicId : undefined,
    // eslint-disable-next-line
  }), [rowsPerPage, page, selectedTab, debouncedSearchText, stockTransferStore.applyFiltersTrigger, ClinicId]);
  const { data: ReceivedList, isLoading, isFetching, refetch } = useReceivedList(ReceiveListParams);


  useEffect(() => {
    if (ReceivedList && Array.isArray(ReceivedList.objects)) {
      // Update total count in store
      const count = ReceivedList?.meta?.total_count ?? 0;
      setProspectiveTotalCount(Number(count) || 0);

      const mapped: ReceivedRow[] = (ReceivedList.objects).map((item: ReceivedListResponse) => ({
        id: item.id,
        patient: item.patient ? `${item.patient.first_name} ${item.patient.last_name}` : '-',
        indentNoDate: `${item.indent_no ?? '-'} | ${item.indent_date ? convertDateToDDMMYYYY(item.indent_date) : '-'}`,
        issueNoDate: `${item.issue_no ?? '-'} | ${item.issue_date ? convertDateToDDMMYYYY(item.issue_date) : '-'}`,
        receivedNoDate: `${item.receive_no ?? '-'} | ${item.received_date ? convertDateToDDMMYYYY(item.received_date) : '-'}`,
        fromClinicReceivedStore: `${item.clinic ??  ''} | ${item.to_store_label ?? item.to_store ?? ''}`.trim(),
        toClinicIssuedStore: `${item.from_clinic_label ?? item.from_clinic ?? ''} | ${item?.from_store_label ?? item?.from_store ?? ''}`.trim(),
        totalAmount: item.total_amount ?? '-',
        receivingAgainst: getIssueAgainstChip(Boolean(item.patient)),
        receiveType: getStatusChip(item.status),
        created_by: item.created_by,
        remark : item.remark,
        indent_details: item.receive_item_details?.map((d) => ({
          ...d,
          Taxes: d.taxes
        })) as unknown as IndentDetail[],
        to_store_id: item.to_store_id,
      }));
      setReceivedTableData(mapped);
    } else {
      setProspectiveTotalCount(0);
      setReceivedTableData([]);
    }
    // eslint-disable-next-line
  }, [ReceivedList, setProspectiveTotalCount]);


  



  const ProspectiveFilterTabs = [
    { key: 'ALL', label: tStockTransfer('All'), count: ReceivedList?.status_counts?.ALL ?? 0 },
    { key: 'DIRECT', label: tStockTransfer('Direct'), count: ReceivedList?.status_counts?.DIRECT ?? 0 },
    { key : 'INDENT', label: tStockTransfer('Indent'), count: ReceivedList?.status_counts?.INDENT ?? 0 },
    { key: 'PR', label: tStockTransfer('PR'), count: ReceivedList?.status_counts?.PR ?? 0 },
  ];

  return(
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid2 container alignItems="left" justifyContent="space-between" width="100%"  my={1} data-testid="receive-list-title-container">
        <Typography variant="h6" fontWeight={700} data-testid="receive-list-title">
          {tStockTransfer('Received Items List')}
        </Typography>
        
      </Grid2>
      <Grid2 py={2} data-testid="receive-list-filter-tabs-container">
        <Box display="flex" gap={1.5} flexWrap="wrap">
          {ProspectiveFilterTabs.map((t) => {
            const isActive = selectedTab === t.key;
            return (
              <Box
                key={t.key}
                onClick={() => { setSelectedTab(t.key); }}
                data-testid={`receive-list-filter-tab-${t.key.toLowerCase()}`}
                className={`${styles.filterTab} ${isActive ? styles.active : styles.inactive}`}
              >
                {`${t.label} (${t.count})`}
              </Box>
            );
          })}
        </Box>
      </Grid2>

      <Grid2 sx={{ flex: 1, minHeight: 0 }} data-testid="receive-list-table-wrapper">
        <ReceiveListTable
          key={selectedTab}
          columns={BaseProspectiveHeaders}
          data={receivedTableData}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={ReceivedList?.meta?.total_count ?? receivedTableData.length}
          onPageChange={setPage}
          onRefetch={refetch}
          isLoading={isLoading || isFetching}
        />
      </Grid2>

    </Box>
  );

};
export default ReceiveListComponent;